import { Bullet, Label, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  DateAxis,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn, orderBy } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { TIME_UNIT, getChartInterval } from "@/helpers/charts";
import { stringOrNumberToDate } from "@/helpers/formatters";
import Lang from "@/lang";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class ColumnChart extends BaseChart {
  isDateAxis!: boolean;
  interval!: time.ITimeInterval;

  public loadChart(chartName: string, family: string): void {
    super.loadChart(chartName, family);
    this.isDateAxis = this.chartFamily === FAMILY.OVERTIME;
  }

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    if (this.isDateAxis) {
      this.interval = getChartInterval(this.settings.xAxis) as time.ITimeInterval;
    }

    this.createChart();
    this.createRenderers();
    this.createAxes();
    this.createCursor();

    let legendNames: string[] = [];

    const series = this.isDateAxis ? [this.settings.yAxisPrimary] : this.data;

    series.forEach((el: ChartDataRecord, index: number) => {
      const paletteLength = this.palette.length;
      const indexColor = index % paletteLength;
      this.isDateAxis ? legendNames.push(el[this.settings.xAxis] as string) : (legendNames = series);
      this.createColumnSeries(el, indexColor);
    });

    this.showLegend(legendNames, (this.chart as XYChart).series.values as LegendData);
  }

  createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: false,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
        focusable: true,
      })
    );
  }

  createRenderers() {
    const x = AxisRendererX.new(this.root, { minGridDistance: 60 });
    x.labels.template.setAll({
      centerX: p50,
      ...chartFontSettings.label,
    });

    const y = AxisRendererY.new(this.root, {});
    y.labels.template.setAll({ ...chartFontSettings.label });

    this.renderers = { x, y };
  }

  createCursor() {
    const cursor = (this.chart as XYChart).set(
      "cursor",
      XYCursor.new(this.root, {
        behavior: "zoomX",
      })
    );
    cursor.lineY.set("visible", false);
  }

  createAxes() {
    const x = this.isDateAxis
      ? (this.chart as XYChart).xAxes.push(
          DateAxis.new(this.root, {
            renderer: this.renderers.x as AxisRendererX,
            groupData: true,
            groupIntervals: [this.interval],
            markUnitChange: false,
            baseInterval: { timeUnit: TIME_UNIT.DAY, count: 1 },
            dateFormats: getAxisDateFormat(this.settings.xAxis),
            marginTop: 14,
          })
        )
      : (this.chart as XYChart).xAxes.push(
          CategoryAxis.new(this.root, {
            categoryField: this.settings.xAxis,
            renderer: this.renderers.x as AxisRendererX,
            marginTop: 14,
          })
        );

    x.data.setAll(this.data);

    const yPrimary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.y as AxisRenderer,
        min: 0,
        marginRight: 14,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxisPrimary, this.data),
      })
    );

    if (this.module === PLAN_MODULE && this.isDateAxis && this.settings.showDataLabels) {
      if (this.settings.xAxis === "week") {
        const xAxis = x as DateAxis<AxisRendererX>;
        this.root.events.once("frameended", () => {
          const privateMin = xAxis.getPrivate("min");
          const privateMax = xAxis.getPrivate("max");
          const privateStep = xAxis.getPrivate("step");
          if (
            typeof privateMin === "number" &&
            typeof privateMax === "number" &&
            typeof privateStep === "number" &&
            privateStep > 0
          ) {
            xAxis.set("min", privateMin - privateStep);
            xAxis.set("max", privateMax + privateStep);
            xAxis.set("strictMinMax", true);
          }
        });
      }

      if (this.settings.xAxis === "week" || this.settings.xAxis === "quarter") {
        const yAxis = yPrimary as ValueAxis<AxisRendererY>;
        this.root.events.once("frameended", () => {
          const privateMax = yAxis.getPrivate("max");
          const privateStep = yAxis.getPrivate("step");
          if (typeof privateMax === "number" && typeof privateStep === "number" && privateStep > 0) {
            yAxis.set("max", privateMax + privateStep);
            yAxis.set("strictMinMax", true);
          }
        });
      }
    }

    if (!this.isDateAxis) {
      const xRenderer = this.renderers.x as AxisRendererX;
      xRenderer.set("minGridDistance", 1);

      const getCategoryCellWidth = () => {
        const plotWidth = (this.chart as XYChart).plotContainer.getPrivate("width") ?? 0;
        const count = x?.dataItems?.length ?? 0;
        return count > 0 ? plotWidth / count : plotWidth;
      };

      const updateXAxisLabelsLayout = () => {
        const cell = getCategoryCellWidth();
        const padding = 16;
        const maxWidth = Math.max(30, Math.floor(cell - padding));

        const behavior = cell < 70 ? "truncate" : "wrap";

        xRenderer.labels.template.setAll({
          ...chartFontSettings.label,
          textAlign: "center",
          maxWidth,
          rotation: 0,
          centerX: p50,
          centerY: 0.5,
          oversizedBehavior: behavior,
          tooltipText: `{${this.settings.xAxis}}`,
          visible: true,
        });
      };

      (this.chart as XYChart).plotContainer.onPrivate("width", updateXAxisLabelsLayout);
      (this.chart as XYChart).plotContainer.onPrivate("height", updateXAxisLabelsLayout);

      if ("events" in x) {
        (x as CategoryAxis<AxisRendererX>).events.on("datavalidated", updateXAxisLabelsLayout);
      }

      updateXAxisLabelsLayout();
    }

    if (this.settings.xAxisTitle) {
      x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    if (this.settings.yAxisPrimaryTitle) {
      yPrimary.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisPrimaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { x, yPrimary };
  }

  createColumnSeries(item: ChartDataRecord, index: number) {
    const seriesConfig = {
      name: this.isDateAxis ? this.settings.yAxisPrimary : (item[this.settings.xAxis] as string),
      xAxis: this.axes.x,
      yAxis: this.axes.yPrimary,
      valueYField: this.settings.yAxisPrimary,
      [this.isDateAxis ? "valueXField" : "categoryXField"]: this.settings.xAxis,
      fill: this.getSeriesColor(index),
    };

    if (!this.isDateAxis) {
      seriesConfig.clustered = false;
    }

    const series: ColumnSeries = (this.chart as XYChart).series.push(ColumnSeries.new(this.root, seriesConfig));

    if (this.settings.showDataLabels) {
      const metricFormatter = this.formatters?.[this.settings.yAxisPrimary];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      series.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerX: p50,
          dy: -30,
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueY");
          if (typeof value !== "number") {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(value, formatterConfig);
          }

          return this.root.numberFormatter.format(value, formatterConfig as any);
        });

        label.adapters.add("visible", () => {
          const v = dataItem?.get("valueY");
          return typeof v === "number" ? v !== 0 : !!v;
        });

        return Bullet.new(this.root, {
          locationY: 1,
          sprite: label,
        });
      });
    }

    this.createTooltip(series as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (this.isDateAxis || series.get("valueYField") === key || series.get("categoryXField") === key) {
            param.push({ key, value });
          }
        });
      }
      return param;
    });

    if (this.isDateAxis) {
      this.createLegendLabel(series, this.settings.yAxisPrimary);
      this.updateColumnSeriesData(this.settings.yAxisPrimary, series);
    } else {
      series.data.push(item);
    }
  }

  updateColumnSeriesData(seriesName: string, series: ColumnSeries): void {
    const data = this.isDateAxis
      ? this.data.map((el: ChartDataRecord) => ({
          [this.settings.xAxis]: stringOrNumberToDate(el[this.settings.xAxis] as string).getTime(),
          [seriesName]: el[seriesName],
        }))
      : this.data;

    series.data.setAll(data);
  }

  mapData(data: ChartDataRecord[]) {
    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (
      this.module === PLAN_MODULE &&
      this.chartFamily !== FAMILY.OVERTIME &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems)
    ) {
      const decompositionMetric = this.settings.xAxis;
      const displayMetric = this.settings.yAxisPrimary;

      preparedData = this.prepareTopPlanModuleData(
        preparedData,
        decompositionMetric,
        displayMetric,
        this.settings.showItems
      );
    }

    preparedData = preparedData.map((item) => ({
      [this.settings.xAxis]: item[this.settings.xAxis] ?? Lang.null,
      [this.settings.yAxisPrimary]: item[this.settings.yAxisPrimary] ?? 0,
    }));

    if (!this.isDateAxis) {
      preparedData = orderBy(preparedData, [this.settings.yAxisPrimary], ["desc"]) as ChartDataRecord[];
      preparedData = this.showItems(preparedData, this.settings.xAxis, this.settings.yAxisPrimary);
    }

    return preparedData;
  }
}
